import React, { useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  CheckCircle, Truck, MapPin, Navigation, Flag, Package, Building2,
  Weight, Box, DollarSign, Calendar, Clock, Info, Phone, Mail, User, AlertCircle,
  AlertTriangle, Wrench, CloudRain, FileText, Users, HelpCircle
} from 'lucide-react';
import { selectCarrierLoadById } from '../store/slices/loadCarrierSlice';
import { selectLoadById } from '../store/slices/loadSlice';
import { formatCurrency, formatDate, formatTime, formatDateTime, formatTimeAgo, parseToDate } from '../utils/formatters';

// Exception type icons and colors
const EXCEPTION_CONFIG = {
  delay: { icon: Clock, color: 'amber' },
  late_arrival: { icon: Clock, color: 'red' },
  weather: { icon: CloudRain, color: 'blue' },
  mechanical: { icon: Wrench, color: 'red' },
  documentation: { icon: FileText, color: 'purple' },
  shipper_issue: { icon: Users, color: 'orange' },
  receiver_issue: { icon: Users, color: 'orange' },
  other: { icon: HelpCircle, color: 'gray' },
  manual: { icon: AlertTriangle, color: 'amber' }
};

const SEVERITY_COLORS = {
  low: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100' },
  medium: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100' },
  high: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100' },
  critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100' }
};

// Exception Alert Component
const ExceptionAlert = ({ exception, onResolve }) => {
  const config = EXCEPTION_CONFIG[exception.exception_type] || EXCEPTION_CONFIG.manual;
  const severity = SEVERITY_COLORS[exception.severity] || SEVERITY_COLORS.medium;
  const Icon = config.icon;
  
  return (
    <div className={`
      p-4 rounded-xl border ${severity.border} ${severity.bg}
      ${exception.is_resolved ? 'opacity-60' : ''}
    `}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${severity.badge}`}>
          <Icon className={`w-5 h-5 ${severity.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`font-medium ${severity.text}`}>
              {exception.exception_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${severity.badge} ${severity.text}`}>
              {exception.severity?.charAt(0).toUpperCase() + exception.severity?.slice(1)}
            </span>
            {exception.is_automated && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                Automated
              </span>
            )}
            {exception.is_resolved && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                ✓ Resolved
              </span>
            )}
          </div>
          <p className="text-sm text-gray-700 mb-2">{exception.notes}</p>
          <p className="text-xs text-gray-500">
            {formatDateTime(exception.created_at)}
            {exception.created_by_name && ` • by ${exception.created_by_name}`}
          </p>
        </div>
        {!exception.is_resolved && onResolve && (
          <button
            onClick={() => onResolve(exception)}
            className="px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg text-sm
                     font-medium hover:bg-emerald-200 transition-colors"
          >
            Resolve
          </button>
        )}
      </div>
    </div>
  );
};

const TimelineStatus = ({ status, isActive, isLast, actualTimestamp, updatedBy }) => {
  const Icon = status.icon;
  
  return (
    <div className="relative flex group">
      {/* Status Line - adjusted for mobile */}
      {!isLast && (
        <div className="absolute left-4 sm:left-5 top-10 w-[2px] h-[calc(100%+2rem)]">
          <div className={`
            absolute inset-0
            ${isActive ? 'animate-gradient-flow bg-gradient-to-b from-blue-500 via-indigo-500 to-gray-200' : 'bg-gray-200'}
          `} />
        </div>
      )}
      
      {/* Status Content - adjusted spacing for mobile */}
      <div className="flex-1 flex gap-3 sm:gap-6">
        {/* Icon Container - smaller on mobile */}
        <div className="relative z-10">
          <div className={`
            w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center
            transition-all duration-500 transform
            ${isActive 
              ? 'bg-gradient-to-br from-blue-500 via-indigo-500 to-blue-600 ring-2 sm:ring-4 ring-blue-100 scale-110 hover:scale-125' 
              : 'bg-white ring-2 sm:ring-4 ring-gray-100 hover:scale-110'
            }
            shadow-lg hover:shadow-xl
          `}>
            <Icon className={`
              w-4 h-4 sm:w-5 sm:h-5 
              ${isActive ? 'text-white animate-pulse' : 'text-gray-400'}
            `} />
          </div>
        </div>

        {/* Content Column - adjusted for mobile */}
        <div className="flex-1 pb-6 sm:pb-8">
          <div className={`
            font-semibold text-base sm:text-lg mb-1 sm:mb-2 transition-all duration-300
            ${isActive ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600' : 'text-gray-500'}
          `}>
            {status.label}
          </div>
          
          {status.subLabel && (
            <div className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 animate-fade-in">
              {status.subLabel}
            </div>
          )}

          {/* Details Card - Restructured for clearer expected vs actual */}
          {(status.details || actualTimestamp) && (
            <div className={`
              backdrop-blur-sm rounded-xl
              transition-all duration-500 transform
              ${isActive 
                ? 'bg-gradient-to-br from-white/90 to-blue-50/90 border border-blue-100/50 shadow-xl hover:shadow-2xl hover:-translate-y-1' 
                : 'bg-gray-50/90 border border-gray-100/50'
              }
            `}>
              {/* Expected Details Section */}
              {status.details && (
                <div className="p-4 sm:p-6">
                  <div className="text-sm font-medium text-gray-700 mb-3">Expected Details</div>
                  {status.details.map((group, groupIdx) => (
                    group.type === 'divider' ? (
                      <div key={`divider-${groupIdx}`} className="my-6 border-t border-gray-100" />
                    ) : (
                      <div key={groupIdx} className="mb-6 last:mb-0">
                        <div className="text-sm font-medium text-gray-600 mb-3">{group.group}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                          {group.items.map((detail, idx) => (
                            <div key={idx} className="flex items-center gap-2 sm:gap-3 group">
                              <div className={`
                                w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center
                                transition-all duration-300
                                ${isActive 
                                  ? 'bg-gradient-to-br from-blue-100 to-indigo-100 group-hover:scale-110' 
                                  : 'bg-gray-100'
                                }
                              `}>
                                <detail.icon className={`
                                  w-3.5 h-3.5 sm:w-4 sm:h-4 transition-all duration-300
                                  ${isActive ? 'text-blue-600 group-hover:rotate-12' : 'text-gray-400'}
                                `} />
                              </div>
                              <div className="transition-all duration-300 group-hover:translate-x-1">
                                <div className="text-xs sm:text-sm text-gray-500">{detail.label}</div>
                                <div className={`
                                  text-sm sm:text-base font-medium
                                  ${detail.value.startsWith('No ') ? 'text-gray-400 italic' : 'text-gray-900'}
                                `}>
                                  {detail.value}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              )}

              {/* Actual Timestamp Section - with divider */}
              {actualTimestamp && (
                <>
                  {status.details && <div className="border-t border-gray-200"></div>}
                  <div className="p-4 sm:p-6">
                    <div className="text-sm font-medium text-gray-700 mb-3">Actual Details</div>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className={`
                        w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center
                        ${isActive ? 'bg-green-100' : 'bg-gray-100'}
                      `}>
                        <Clock className={`
                          w-3.5 h-3.5 sm:w-4 sm:h-4
                          ${isActive ? 'text-green-600' : 'text-gray-400'}
                        `} />
                      </div>
                      <div>
                        <div className="text-sm sm:text-base font-medium text-gray-900">
                          {formatDateTime(actualTimestamp)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatTimeAgo(actualTimestamp)}
                          {updatedBy && ` • Updated by ${updatedBy}`}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LoadStatusTimeline = ({ loadId, isCarrier = false }) => {
  const selectLoadFunction = isCarrier ? selectCarrierLoadById : selectLoadById;
  const load = useSelector(state => selectLoadFunction(state, loadId));
  
  // Add state to force re-renders when load data updates
  const [lastLoadUpdate, setLastLoadUpdate] = useState(Date.now());
  
  // Watch for load data changes and update timestamp
  useEffect(() => {
    if (load) {
      setLastLoadUpdate(Date.now());
    }
  }, [load?.status, load?.assigned_driver, load?.vehicle_info, load?.trailer_info, load?.status_history]);

  const getStatusConfig = useCallback((load) => {
    if (!load || !load.stops) return [];

    // Get status history map for quick lookup
    const statusHistory = (load.status_history || []).reduce((acc, history) => {
      const parsed = parseToDate(history.timestamp);
      acc[history.status] = {
        timestamp: parsed || null,
        updatedBy: history.user_type === 'driver' 
          ? `Driver ${history.driver?.name || 'Unknown'}`
          : `${history.user_type} ${history.user?.name || 'Unknown'}`
      };
      return acc;
    }, {});

    const sortedStops = [...load.stops].sort((a, b) => a.sequence - b.sequence);

    // Normalize stops to ensure first pickup and last delivery reflect load-level schedule
    const pickupIndices = sortedStops
      .map((s, idx) => ({ s, idx }))
      .filter(({ s }) => s.stop_type === 'pickup')
      .map(({ idx }) => idx);
    const deliveryIndices = sortedStops
      .map((s, idx) => ({ s, idx }))
      .filter(({ s }) => s.stop_type === 'delivery')
      .map(({ idx }) => idx);

    const firstPickupIdx = pickupIndices.length > 0 ? pickupIndices[0] : 0;
    const lastDeliveryIdx = deliveryIndices.length > 0 ? deliveryIndices[deliveryIndices.length - 1] : (sortedStops.length - 1);

    const normalizedStops = sortedStops.map((stop, idx) => {
      // Copy to avoid mutating Redux state
      const updated = { ...stop };
      if (idx === firstPickupIdx) {
        if (load.pickup_date) updated.date = load.pickup_date;
        if (load.pickup_time) updated.time = load.pickup_time;
      }
      if (idx === lastDeliveryIdx) {
        if (load.delivery_date) updated.date = load.delivery_date;
        if (load.delivery_time) updated.time = load.delivery_time;
      }
      return updated;
    });
    
    // Build timeline with actual timestamps only for non-stop sequences
    const timeline = [
      {
        key: 'assigned',
        label: 'Assigned',
        icon: Building2,
        sequence: -3,
        actualTimestamp: statusHistory['assigned']?.timestamp,
        updatedBy: statusHistory['assigned']?.updatedBy,
      },
      {
        key: 'assigned_driver',
        label: 'Driver Assigned',
        icon: Truck,
        sequence: -2,
        actualTimestamp: statusHistory['assigned_driver']?.timestamp,
        updatedBy: statusHistory['assigned_driver']?.updatedBy,
      },
      {
        key: 'trip_started',
        label: 'Trip Started',
        icon: Navigation,
        sequence: -1,
        actualTimestamp: statusHistory['trip_started']?.timestamp,
        updatedBy: statusHistory['trip_started']?.updatedBy,
        subLabel: `En route to first pickup in ${sortedStops[0]?.city}, ${sortedStops[0]?.state}`,
      }
    ];

    // Modify the stops loop to include both expected and actual details
    normalizedStops.forEach((stop, index) => {
      const isLastStop = index === sortedStops.length - 1;
      const stopStatus = `at_stop_${stop.sequence}`;
      const transitStatus = !isLastStop ? `in_transit_${stop.sequence}_${normalizedStops[index + 1].sequence}` : null;

      // Determine display schedule values with hard override for first pickup/last delivery
      const isFirstPickup = index === firstPickupIdx;
      const isLastDelivery = index === lastDeliveryIdx;
      const displayDate = isFirstPickup ? (load.pickup_date || stop.date) : (isLastDelivery ? (load.delivery_date || stop.date) : stop.date);
      const displayTime = isFirstPickup ? (load.pickup_time || stop.time) : (isLastDelivery ? (load.delivery_time || stop.time) : stop.time);

      timeline.push({
        key: stopStatus,
        label: `${stop.stop_type === 'pickup' ? 'At Pickup' : 'At Delivery'} - ${stop.city}`,
        icon: stop.stop_type === 'pickup' ? MapPin : (isLastStop ? Flag : Navigation),
        sequence: stop.sequence,
        subLabel: `${stop.address}, ${stop.city}, ${stop.state}`,
        actualTimestamp: statusHistory[stopStatus]?.timestamp,
        updatedBy: statusHistory[stopStatus]?.updatedBy,
        details: [
          // Scheduling Details Group
          { 
            group: 'Schedule',
            items: [
              { icon: Calendar, label: 'Date', value: formatDate(displayDate) },
              { icon: Clock, label: 'Time', value: formatTime(displayTime) },
              stop.special_requirements && { 
                icon: Info, 
                label: 'Requirements', 
                value: stop.special_requirements 
              }
            ].filter(Boolean)
          },
          // Add divider between groups
          { type: 'divider' },
          // Modified Contact Details Group
          { 
            group: 'Contact Information',
            items: [
              // Add business name as first item if present
              { 
                icon: Building2, 
                label: 'Business', 
                value: stop.business_name || 'No business name'
              },
              { 
                icon: User, 
                label: 'Contact', 
                value: stop.contact_name || 'No contact specified'
              },
              { 
                icon: Phone, 
                label: 'Phone', 
                value: stop.contact_phone || 'No phone number'
              },
              { 
                icon: Mail, 
                label: 'Email', 
                value: stop.contact_email || 'No email provided'
              },
              { 
                icon: AlertCircle, 
                label: 'Instructions', 
                value: stop.contact_instructions || 'No special instructions'
              }
            ]
          }
        ]
      });

      if (transitStatus) {
        timeline.push({
          key: transitStatus,
          label: `In Transit to ${sortedStops[index + 1].city}`,
          icon: Truck,
          sequence: stop.sequence + 0.5,
          subLabel: `En route to ${sortedStops[index + 1].city}, ${sortedStops[index + 1].state}`,
          actualTimestamp: statusHistory[transitStatus]?.timestamp,
          updatedBy: statusHistory[transitStatus]?.updatedBy,
        });
      }
    });

    // Add completed status if relevant
    if ((load.status || '').toLowerCase() === 'completed') {
      timeline.push({
        key: 'completed',
        label: 'Completed',
        icon: CheckCircle,
        sequence: 999,
        actualTimestamp: statusHistory['completed']?.timestamp,
        updatedBy: statusHistory['completed']?.updatedBy,
      });
    }

    return timeline;
  }, []);

  const parseCurrentStatus = useCallback((status) => {
    if (!status) return null;
    
    const statusStr = String(status).toLowerCase();
    
    // Base statuses
    if (statusStr === 'assigned') {
      return { type: 'assigned', sequence: -3 };
    }
    if (statusStr === 'assigned_driver') {
      return { type: 'assigned_driver', sequence: -2 };
    }
    if (statusStr === 'trip_started') {
      return { type: 'trip_started', sequence: -1 };
    }
    if (statusStr === 'completed') {
      return { type: 'completed', sequence: 1000 };
    }
    
    // Stop status
    if (statusStr.startsWith('at_stop_')) {
      const parts = statusStr.split('_');
      const sequence = parseInt(parts[2]);
      return {
        type: 'at_stop',
        sequence: sequence
      };
    }
    
    // Transit status
    if (statusStr.startsWith('in_transit_')) {
      const parts = statusStr.split('_');
      const fromSeq = parseInt(parts[2]);
      const toSeq = parseInt(parts[3]);
      return {
        type: 'in_transit',
        sequence: fromSeq,
        fromSequence: fromSeq,
        toSequence: toSeq
      };
    }
    
    return null;
  }, []);
  
  const isStatusActive = useCallback((status, currentStatus) => {
    const parsedCurrent = parseCurrentStatus(currentStatus);
    if (!parsedCurrent) return false;

    const statusKey = status?.key || status;
    const parsedStatus = parseCurrentStatus(statusKey);
    
    if (!parsedStatus) return false;

    // Helper function to get sequence number
    const getBaseSequence = (parsed) => {
      const seq = {
        'assigned': -3,
        'assigned_driver': -2,
        'trip_started': -1,
        'at_stop': parsed.sequence,
        'in_transit': parsed.sequence,
        'completed': 1000
      }[parsed.type];
      
      return seq ?? -4;
    };

    const currentSeq = getBaseSequence(parsedCurrent);
    const statusSeq = getBaseSequence(parsedStatus);

    // If checking a transit status
    if (parsedStatus.type === 'in_transit') {
      // If we're currently in transit, show all previous transit segments as active
      if (parsedCurrent.type === 'in_transit') {
        return parsedStatus.toSequence <= parsedCurrent.toSequence;
      }
      // If we're at a stop, show all transit segments up to this stop
      if (parsedCurrent.type === 'at_stop') {
        return parsedStatus.toSequence <= parsedCurrent.sequence;
      }
      // For completed status, show all transit segments
      if (parsedCurrent.type === 'completed') {
        return true;
      }
      return false;
    }

    // For stop statuses
    if (parsedStatus.type === 'at_stop') {
      // If we're in transit, all stops up to and including the 'from' stop should be active
      if (parsedCurrent.type === 'in_transit') {
        return parsedStatus.sequence <= parsedCurrent.fromSequence;
      }
      // If we're at a stop, all previous stops and current stop should be active
      if (parsedCurrent.type === 'at_stop') {
        return parsedStatus.sequence <= currentSeq;
      }
    }

    // For completed status, show all stops
    if (parsedCurrent.type === 'completed') {
      return true;
    }

    // For other status types (assigned, etc.), simple sequence comparison
    return statusSeq <= currentSeq;
  }, [parseCurrentStatus]);

  const statusConfig = getStatusConfig(load);
  
  // Get active exceptions from load
  const activeExceptions = (load?.exceptions || []).filter(e => !e.is_resolved);
  const resolvedExceptions = (load?.exceptions || []).filter(e => e.is_resolved);
  const hasExceptions = (load?.exceptions || []).length > 0;

  return (
    <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-sm">
      {/* Fixed Header - adjusted padding for mobile */}
      <div className="p-4 sm:p-8 pb-3 sm:pb-4 border-b">
        <div className="flex items-center justify-between">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Load Progress</h3>
          {activeExceptions.length > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
              <AlertTriangle className="w-3.5 h-3.5" />
              {activeExceptions.length} Active {activeExceptions.length === 1 ? 'Exception' : 'Exceptions'}
            </span>
          )}
        </div>
      </div>
      
      {/* Active Exceptions Alert Section */}
      {activeExceptions.length > 0 && (
        <div className="p-4 sm:p-8 py-4 bg-red-50/50 border-b border-red-100">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h4 className="font-semibold text-red-800">Active Exceptions</h4>
          </div>
          <div className="space-y-3">
            {activeExceptions.map((exception) => (
              <ExceptionAlert 
                key={exception.id} 
                exception={exception}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Scrollable Timeline - adjusted padding for mobile */}
      <div className="p-4 sm:p-8 pt-3 sm:pt-4 max-h-[600px] overflow-y-auto">
        <div className="space-y-6 sm:space-y-8">
          {statusConfig.map((status, index) => (
            <TimelineStatus
              key={status.key}
              status={status}
              isActive={isStatusActive(status, load?.status)}
              isLast={index === statusConfig.length - 1}
              actualTimestamp={status.actualTimestamp}
              updatedBy={status.updatedBy}
            />
          ))}
        </div>
        
        {/* Resolved Exceptions Section */}
        {resolvedExceptions.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <h4 className="font-semibold text-gray-700">
                Resolved Exceptions ({resolvedExceptions.length})
              </h4>
            </div>
            <div className="space-y-3">
              {resolvedExceptions.map((exception) => (
                <ExceptionAlert 
                  key={exception.id} 
                  exception={exception}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadStatusTimeline;
